import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Card,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { apiGetConfig, apiSaveConfig, apiLogout, apiListAssets, apiUploadAsset } from "./adminApi";

function ImagePicker({ label, value, onChange, assets, onAssetUploaded, showToast }) {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { path: uploadedPath } = await apiUploadAsset(file);
      onChange(uploadedPath);
      onAssetUploaded(uploadedPath);
      showToast("Image uploaded successfully!");
    } catch (err) {
      showToast("Upload failed: " + err.message, "error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{label}</InputLabel>
        <Select value={value || ""} label={label} onChange={(e) => onChange(e.target.value)}>
          {assets.map((p) => (
            <MenuItem key={p} value={p}>{p.replace("/assets/", "")}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: value ? 2 : 0 }}>
        <TextField label="Or Custom Image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} fullWidth />
        <Button
          variant="outlined"
          component="label"
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
          sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 56 }}
        >
          {uploading ? "Uploading" : "Upload"}
          <input type="file" accept="image/*" hidden onChange={handleFileChange} />
        </Button>
      </Box>

      {value && (
        <Box
          component="img"
          src={value}
          alt="preview"
          sx={{ maxHeight: 140, borderRadius: "8px", border: "1px solid #ddd", display: "block" }}
        />
      )}
    </Box>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [assets, setAssets] = useState([]);

  // Modals state
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState(""); // "hero", "feature", "success", "upcoming", "recent"
  const [editIndex, setEditIndex] = useState(-1);

  useEffect(() => {
    fetchConfigAndAssets();
  }, []);

  async function fetchConfigAndAssets() {
    setLoading(true);
    setError("");
    try {
      const data = await apiGetConfig();
      setConfig(data);
      const assetFiles = await apiListAssets();
      setAssets(assetFiles);
    } catch (err) {
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const handleAssetUploaded = (newPath) => {
    setAssets((prev) => (prev.includes(newPath) ? prev : [newPath, ...prev]));
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
      onLogout();
    } catch (err) {
      showToast("Logout failed: " + err.message, "error");
    }
  };

  const handleSaveAll = async () => {
    setSaveLoading(true);
    try {
      await apiSaveConfig(config);
      showToast("Configuration saved successfully!");
    } catch (err) {
      showToast("Save failed: " + err.message, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  // Re-ordering items helper
  const moveItem = (section, index, direction) => {
    const list = [...config[section]];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    const temp = list[index];
    list[index] = list[targetIndex];
    list[targetIndex] = temp;
    setConfig({ ...config, [section]: list });
  };

  // Delete helpers
  const deleteItem = (section, index) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    const list = [...config[section]];
    list.splice(index, 1);
    setConfig({ ...config, [section]: list });
    showToast("Item deleted locally. Remember to click Save!");
  };

  // Add/Edit Dialog Helpers
  const openAddDialog = (type) => {
    setEditType(type);
    setEditIndex(-1);
    if (type === "hero") {
      setEditItem({ img: "/assets/upcoming_Event.jpeg", title: "", subtitle: "" });
    } else if (type === "feature") {
      setEditItem({ icon: "VerifiedUser", title: "", desc: "" });
    } else if (type === "success") {
      setEditItem({ id: Date.now(), img: "/assets/SuccessStory_4.jpg", title: "", story: "" });
    } else if (type === "upcoming") {
      setEditItem({
        id: Date.now(),
        title: "",
        titleEn: "",
        date: "",
        time: "",
        venue: "",
        fee: "",
        image: "/assets/upcoming_Event.jpeg",
        bannerImage: "/assets/upcoming_Event.jpeg",
        whatsappNumber: "8903791212",
        contactNumbers: ["8903791212"],
        specialGuest: "",
        notes: ""
      });
    } else if (type === "recent") {
      setEditItem({ video: "", link: "", title: "" });
    }
  };

  const openEditDialog = (type, index) => {
    setEditType(type);
    setEditIndex(index);
    if (type === "hero") {
      setEditItem({ ...config.hero.slides[index] });
    } else if (type === "feature") {
      setEditItem({ ...config.usp.features[index] });
    } else if (type === "success") {
      setEditItem({ ...config.successStories[index] });
    } else if (type === "upcoming") {
      const item = config.upcomingEvents[index];
      setEditItem({
        ...item,
        contactNumbers: item.contactNumbers ? [...item.contactNumbers] : ["8903791212"]
      });
    } else if (type === "recent") {
      setEditItem({ ...config.recentEvents[index] });
    }
  };

  const handleSaveDialogItem = () => {
    if (editIndex === -1) {
      // Add new
      if (editType === "hero") {
        const slides = [...(config.hero?.slides || [])];
        slides.push(editItem);
        setConfig({ ...config, hero: { ...config.hero, slides } });
      } else if (editType === "feature") {
        const features = [...(config.usp?.features || [])];
        features.push(editItem);
        setConfig({ ...config, usp: { ...config.usp, features } });
      } else if (editType === "success") {
        const successStories = [...(config.successStories || [])];
        successStories.push(editItem);
        setConfig({ ...config, successStories });
      } else if (editType === "upcoming") {
        const upcomingEvents = [...(config.upcomingEvents || [])];
        upcomingEvents.push(editItem);
        setConfig({ ...config, upcomingEvents });
      } else if (editType === "recent") {
        const recentEvents = [...(config.recentEvents || [])];
        recentEvents.push(editItem);
        setConfig({ ...config, recentEvents });
      }
    } else {
      // Edit existing
      if (editType === "hero") {
        const slides = [...config.hero.slides];
        slides[editIndex] = editItem;
        setConfig({ ...config, hero: { ...config.hero, slides } });
      } else if (editType === "feature") {
        const features = [...config.usp.features];
        features[editIndex] = editItem;
        setConfig({ ...config, usp: { ...config.usp, features } });
      } else if (editType === "success") {
        const successStories = [...config.successStories];
        successStories[editIndex] = editItem;
        setConfig({ ...config, successStories });
      } else if (editType === "upcoming") {
        const upcomingEvents = [...config.upcomingEvents];
        upcomingEvents[editIndex] = editItem;
        setConfig({ ...config, upcomingEvents });
      } else if (editType === "recent") {
        const recentEvents = [...config.recentEvents];
        recentEvents[editIndex] = editItem;
        setConfig({ ...config, recentEvents });
      }
    }
    setEditItem(null);
    showToast("Change applied locally. Save to finalize.");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", gap: 2 }}>
        <CircularProgress sx={{ color: "#BF0603" }} />
        <Typography variant="body1" sx={{ fontFamily: "Quicksand", fontWeight: 700 }}>Loading admin configuration...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={fetchConfigAndAssets} startIcon={<RefreshIcon />}>Retry Loading</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8f9fa", display: "flex", flexDirection: "column" }}>
      {/* Admin Navbar */}
      <Box
        sx={{
          backgroundColor: "#1a0a0a",
          color: "#fff",
          px: { xs: 2, md: 4 },
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid #D4AF37",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontFamily: "Quicksand", fontWeight: 800, color: "#fff" }}>
            Vivaha Sangamam Admin
          </Typography>
          <Typography variant="caption" sx={{ color: "#D4AF37", fontWeight: 700 }}>
            WEBSITE CONFIGURATION PANEL
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={saveLoading}
            sx={{ fontWeight: 700, fontFamily: "Quicksand", textTransform: "none" }}
          >
            Save All Changes
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              borderColor: "rgba(255,255,255,0.3)",
              color: "#fff",
              fontWeight: 700,
              fontFamily: "Quicksand",
              textTransform: "none",
              "&:hover": { borderColor: "#BF0603", backgroundColor: "rgba(191,6,3,0.1)" },
            }}
          >
            Log Out
          </Button>
        </Box>
      </Box>

      {/* Main Panel Content */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
            "& .MuiTab-root": { fontWeight: 700, fontFamily: "Quicksand" },
            "& .Mui-selected": { color: "#BF0603 !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#BF0603" },
          }}
        >
          <Tab label="Hero Slider" />
          <Tab label="USP / Why Choose Us" />
          <Tab label="Success Stories" />
          <Tab label="Upcoming Events" />
          <Tab label="Recent Events (Highlights)" />
          <Tab label="YouTube Settings" />
        </Tabs>

        {/* ── TAB 1: HERO SLIDER ────────────────────────────────────────────── */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Quicksand" }}>Hero Banner Slides</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog("hero")} sx={{ backgroundColor: "#BF0603", "&:hover": { backgroundColor: "#9e0402" } }}>
              Add Slide
            </Button>
          </Box>
          <Grid container spacing={3}>
            {config.hero?.slides?.map((slide, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)", height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ height: 180, position: "relative", backgroundColor: "#eaeaea" }}>
                    <img src={slide.img} alt={slide.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </Box>
                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 0.5 }}>{slide.title || "No Title"}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>{slide.subtitle || "No Subtitle"}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fafafa" }}>
                    <Box>
                      <IconButton size="small" onClick={() => moveItem("hero", index, -1)} disabled={index === 0}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveItem("hero", index, 1)} disabled={index === config.hero.slides.length - 1}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => openEditDialog("hero", index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteItem("hero", index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* ── TAB 2: USP / WHY CHOOSE US ────────────────────────────────────── */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Quicksand", mb: 2 }}>Main Statistics</Typography>
            <Grid container spacing={3}>
              {config.usp?.stats?.map((stat, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(0,0,0,0.08)" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          label="Value"
                          type="number"
                          value={stat.value}
                          onChange={(e) => {
                            const stats = [...config.usp.stats];
                            stats[index].value = parseInt(e.target.value) || 0;
                            setConfig({ ...config, usp: { ...config.usp, stats } });
                          }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Suffix"
                          value={stat.suffix}
                          onChange={(e) => {
                            const stats = [...config.usp.stats];
                            stats[index].suffix = e.target.value;
                            setConfig({ ...config, usp: { ...config.usp, stats } });
                          }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Label"
                          value={stat.label}
                          onChange={(e) => {
                            const stats = [...config.usp.stats];
                            stats[index].label = e.target.value;
                            setConfig({ ...config, usp: { ...config.usp, stats } });
                          }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Quicksand" }}>Feature Lists</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog("feature")} sx={{ backgroundColor: "#BF0603", "&:hover": { backgroundColor: "#9e0402" } }}>
                Add Feature
              </Button>
            </Box>
            <Grid container spacing={3}>
              {config.usp?.features?.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(0,0,0,0.08)", height: "100%", display: "flex", flexDirection: "column" }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 0.5 }}>{feature.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: "bold" }}>Icon: {feature.icon}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 2 }}>{feature.desc}</Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box>
                        <IconButton size="small" onClick={() => moveItem("feature", index, -1)} disabled={index === 0}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => moveItem("feature", index, 1)} disabled={index === config.usp.features.length - 1}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box>
                        <IconButton size="small" color="primary" onClick={() => openEditDialog("feature", index)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteItem("feature", index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* ── TAB 3: SUCCESS STORIES ────────────────────────────────────────── */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Quicksand" }}>Success Stories</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog("success")} sx={{ backgroundColor: "#BF0603", "&:hover": { backgroundColor: "#9e0402" } }}>
              Add Story
            </Button>
          </Box>
          <Grid container spacing={3}>
            {config.successStories?.map((story, index) => (
              <Grid item xs={12} md={4} key={story.id || index}>
                <Card sx={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)", height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ height: 180, position: "relative", backgroundColor: "#eaeaea" }}>
                    <img src={story.img} alt={story.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </Box>
                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 0.5 }}>{story.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {story.story}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fafafa" }}>
                    <Box>
                      <IconButton size="small" onClick={() => moveItem("success", index, -1)} disabled={index === 0}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveItem("success", index, 1)} disabled={index === config.successStories.length - 1}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => openEditDialog("success", index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteItem("success", index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* ── TAB 4: UPCOMING EVENTS ────────────────────────────────────────── */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Quicksand" }}>Upcoming Swayamvaram Events</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog("upcoming")} sx={{ backgroundColor: "#BF0603", "&:hover": { backgroundColor: "#9e0402" } }}>
              Add Event
            </Button>
          </Box>
          <Grid container spacing={3}>
            {config.upcomingEvents?.map((event, index) => (
              <Grid item xs={12} key={event.id || index}>
                <Card sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(0,0,0,0.08)" }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={3}>
                      <img src={event.bannerImage || event.image} alt={event.title} style={{ width: "100%", height: "auto", maxHeight: 180, objectFit: "contain", borderRadius: "8px", border: "1px solid #ddd" }} />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: "#BF0603", mb: 1 }}>{event.titleEn || event.title}</Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Date:</strong> {event.date} | <strong>Time:</strong> {event.time}</Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Venue:</strong> {event.venue}</Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Fee:</strong> {event.fee} | <strong>Contacts:</strong> {event.contactNumbers?.join(", ")}</Typography>
                      {event.specialGuest && <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Guest/Feature:</strong> {event.specialGuest}</Typography>}
                      {event.notes && <Typography variant="body2" color="text.secondary"><strong>Notes:</strong> {event.notes}</Typography>}
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, justifyContent: "center", alignItems: "center", gap: 1 }}>
                      <Box>
                        <IconButton size="small" onClick={() => moveItem("upcoming", index, -1)} disabled={index === 0}>
                          <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => moveItem("upcoming", index, 1)} disabled={index === config.upcomingEvents.length - 1}>
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, mt: { xs: 0, sm: 2 } }}>
                        <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => openEditDialog("upcoming", index)}>Edit</Button>
                        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => deleteItem("upcoming", index)}>Delete</Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* ── TAB 5: RECENT EVENTS (VIDEO HIGHLIGHTS) ───────────────────────── */}
        <TabPanel value={activeTab} index={4}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Quicksand" }}>Recent Swayamvaram Highlights</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openAddDialog("recent")} sx={{ backgroundColor: "#BF0603", "&:hover": { backgroundColor: "#9e0402" } }}>
              Add Video Highlight
            </Button>
          </Box>
          <Grid container spacing={3}>
            {config.recentEvents?.map((video, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)", height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#222" }}>
                    {video.link ? (
                      <Typography color="#fff" variant="subtitle2">YouTube Link Set</Typography>
                    ) : (
                      <Typography color="error" variant="subtitle2">No Link</Typography>
                    )}
                  </Box>
                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 0.5 }}>{video.title}</Typography>
                    <Typography variant="caption" sx={{ wordBreak: "break-all", display: "block" }}>Link: {video.link}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fafafa" }}>
                    <Box>
                      <IconButton size="small" onClick={() => moveItem("recent", index, -1)} disabled={index === 0}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveItem("recent", index, 1)} disabled={index === config.recentEvents.length - 1}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => openEditDialog("recent", index)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteItem("recent", index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* ── TAB 6: YOUTUBE SECTION INTEGRATION ────────────────────────────── */}
        <TabPanel value={activeTab} index={5}>
          <Card sx={{ p: 4, borderRadius: "16px", border: "1px solid rgba(0,0,0,0.08)", maxWidth: 700 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "Quicksand", mb: 3 }}>YouTube Channel Showcase Settings</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="YouTube Channel URL"
                  value={config.youtubeSection?.channelUrl || ""}
                  onChange={(e) => {
                    setConfig({
                      ...config,
                      youtubeSection: { ...config.youtubeSection, channelUrl: e.target.value }
                    });
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Playlist ID (usually 'UU' + ChannelID for uploads)"
                  value={config.youtubeSection?.playlistId || ""}
                  onChange={(e) => {
                    setConfig({
                      ...config,
                      youtubeSection: { ...config.youtubeSection, playlistId: e.target.value }
                    });
                  }}
                  fullWidth
                  helperText="Required to pull live videos dynamically on the homepage"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Section Title"
                  value={config.youtubeSection?.channelTitle || ""}
                  onChange={(e) => {
                    setConfig({
                      ...config,
                      youtubeSection: { ...config.youtubeSection, channelTitle: e.target.value }
                    });
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Card>
        </TabPanel>
      </Box>

      {/* ── ADD/EDIT DIALOG ────────────────────────────────────────────────── */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, fontFamily: "Quicksand" }}>
          {editIndex === -1 ? "Add Item" : "Edit Item"} ({editType.toUpperCase()})
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {editItem && editType === "hero" && (
            <>
              <TextField label="Title" value={editItem.title || ""} onChange={(e) => setEditItem({ ...editItem, title: e.target.value })} fullWidth />
              <TextField label="Subtitle" value={editItem.subtitle || ""} onChange={(e) => setEditItem({ ...editItem, subtitle: e.target.value })} fullWidth />
              <ImagePicker
                label="Image Asset"
                value={editItem.img}
                onChange={(v) => setEditItem({ ...editItem, img: v })}
                assets={assets}
                onAssetUploaded={handleAssetUploaded}
                showToast={showToast}
              />
            </>
          )}

          {editItem && editType === "feature" && (
            <>
              <TextField label="Title" value={editItem.title || ""} onChange={(e) => setEditItem({ ...editItem, title: e.target.value })} fullWidth />
              <TextField label="Description" value={editItem.desc || ""} onChange={(e) => setEditItem({ ...editItem, desc: e.target.value })} fullWidth multiline rows={3} />
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select value={editItem.icon || "VerifiedUser"} label="Icon" onChange={(e) => setEditItem({ ...editItem, icon: e.target.value })}>
                  <MenuItem value="VerifiedUser">VerifiedUser</MenuItem>
                  <MenuItem value="ThumbUp">ThumbUp</MenuItem>
                  <MenuItem value="VolunteerActivism">VolunteerActivism</MenuItem>
                  <MenuItem value="TravelExplore">TravelExplore</MenuItem>
                  <MenuItem value="Lock">Lock</MenuItem>
                  <MenuItem value="People">People</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {editItem && editType === "success" && (
            <>
              <TextField label="Couple Names / Title" value={editItem.title || ""} onChange={(e) => setEditItem({ ...editItem, title: e.target.value })} fullWidth />
              <TextField label="Success Story Text" value={editItem.story || ""} onChange={(e) => setEditItem({ ...editItem, story: e.target.value })} fullWidth multiline rows={4} />
              <ImagePicker
                label="Image Asset"
                value={editItem.img}
                onChange={(v) => setEditItem({ ...editItem, img: v })}
                assets={assets}
                onAssetUploaded={handleAssetUploaded}
                showToast={showToast}
              />
            </>
          )}

          {editItem && editType === "upcoming" && (
            <>
              <TextField label="Event Title (English)" value={editItem.titleEn || ""} onChange={(e) => setEditItem({ ...editItem, titleEn: e.target.value, title: e.target.value })} fullWidth required />
              <TextField label="Date (e.g. 12 July 2026)" value={editItem.date || ""} onChange={(e) => setEditItem({ ...editItem, date: e.target.value })} fullWidth />
              <TextField label="Timings (e.g. 09:00 AM – 01:30 PM)" value={editItem.time || ""} onChange={(e) => setEditItem({ ...editItem, time: e.target.value })} fullWidth />
              <TextField label="Venue Address" value={editItem.venue || ""} onChange={(e) => setEditItem({ ...editItem, venue: e.target.value })} fullWidth multiline rows={2} />
              <TextField label="Entry Fee (e.g. ₹500)" value={editItem.fee || ""} onChange={(e) => setEditItem({ ...editItem, fee: e.target.value })} fullWidth />
              <TextField
                label="Contact Numbers (comma separated)"
                value={editItem.contactNumbers?.join(", ") || ""}
                onChange={(e) => setEditItem({ ...editItem, contactNumbers: e.target.value.split(",").map(n => n.trim()) })}
                fullWidth
              />
              <TextField label="Special Feature / Guest Astrologer" value={editItem.specialGuest || ""} onChange={(e) => setEditItem({ ...editItem, specialGuest: e.target.value })} fullWidth />
              <TextField label="Brief Notes" value={editItem.notes || ""} onChange={(e) => setEditItem({ ...editItem, notes: e.target.value })} fullWidth multiline rows={2} />
              <ImagePicker
                label="Poster/Banner Image"
                value={editItem.bannerImage || editItem.image}
                onChange={(v) => setEditItem({ ...editItem, bannerImage: v, image: v })}
                assets={assets}
                onAssetUploaded={handleAssetUploaded}
                showToast={showToast}
              />
            </>
          )}

          {editItem && editType === "recent" && (
            <>
              <TextField label="Title / Meetup Location" value={editItem.title || ""} onChange={(e) => setEditItem({ ...editItem, title: e.target.value })} fullWidth />
              <TextField label="YouTube Video Link" value={editItem.link || ""} onChange={(e) => setEditItem({ ...editItem, link: e.target.value })} fullWidth />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setEditItem(null)} variant="outlined">Cancel</Button>
          <Button onClick={handleSaveDialogItem} variant="contained" sx={{ backgroundColor: "#BF0603", "&:hover": { backgroundColor: "#9e0402" } }}>Apply</Button>
        </DialogActions>
      </Dialog>

      {/* Toast Alert */}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={handleCloseToast} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: "100%", borderRadius: "10px" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
